import * as chatApi from '../../services/chatApi'
import type { ChatSession } from '../../types'
import { emptyDraft } from './stream'
import type {
  ChatStoreCreator,
  Getter,
  SessionsLoaders,
  SessionsMutations,
  Setter,
} from './types'

// After resuming a turn on load, wait this long for stream frames before
// concluding it finished during the load gap and reconciling. Any frame
// (incl. the agent_status heartbeat) resets liveness and keeps it streaming.
const RESUME_RECONCILE_DELAY_MS = 3000

// The pending resume reconcile, cancelled whenever a new session is opened so a
// stale timer from a previous open can't reconcile the wrong session.
let resumeReconcileTimer: ReturnType<typeof setTimeout> | null = null

export const sessionsLoaders: ChatStoreCreator<SessionsLoaders> = (
  set,
  get,
) => ({
  async fetchSessions(slug: string) {
    set({ loadingSessions: true })
    try {
      const sessions = await chatApi.fetchSessions(slug)
      set({ sessions })
    } finally {
      set({ loadingSessions: false })
    }
  },

  async fetchArchivedSessions(slug: string) {
    set({ loadingArchived: true })
    try {
      const archivedSessions = await chatApi.fetchSessions(slug, {
        archived: true,
      })
      set({ archivedSessions })
    } finally {
      set({ loadingArchived: false })
    }
  },

  async openSession(slug: string, sessionId: string) {
    // Cancel a resume reconcile still pending from a previous open so it can't
    // fire against this (or a later) session.
    if (resumeReconcileTimer !== null) {
      clearTimeout(resumeReconcileTimer)
      resumeReconcileTimer = null
    }
    set({
      loadingMessages: true,
      currentSessionId: sessionId,
      messages: [],
      // Reset any in-flight stream state from the previously open session.
      // A turn runs to completion server-side even after we unsubscribe, so
      // its terminal event may never reach us — clearing here prevents a
      // stale spinner or draft from leaking across a session switch.
      draftAssistant: null,
      optimisticUserText: null,
      optimisticUserSessionId: null,
      sendingMessage: false,
      thinkingSince: null,
    })
    try {
      const session = await chatApi.fetchSession(slug, sessionId)
      set({
        messages: session.messages,
        cancelledReportEditTokens: session.cancelledReportEditTokens ?? [],
      })
      if (session.activeTurnId) resumeInFlightTurn(set, get, slug, sessionId)
    } finally {
      set({ loadingMessages: false })
    }
  },
})

// A turn is streaming server-side for the session we just loaded (e.g. a second
// tab opened mid-turn). Show the working state so the chat doesn't look dead:
// real content frames build the draft via `resumeDraftIfMissing`, status frames
// drive the label, and `turn_complete` (which our subscription will receive)
// finalizes with the full answer. If the turn actually finished during the load
// gap, no frames arrive and we reconcile to fetch the persisted turn.
function resumeInFlightTurn(
  set: Setter,
  get: Getter,
  slug: string,
  sessionId: string,
) {
  const resumedAt = Date.now()
  set({
    sendingMessage: true,
    thinkingSince: resumedAt,
    optimisticUserSessionId: sessionId,
    // An empty draft is required for the working bubble to render — without it
    // the thinking status stays hidden and a silent (e.g. subagent) turn still
    // looks dead. Real frames stream into this draft; turn_complete refetches.
    draftAssistant: emptyDraft(sessionId),
  })
  resumeReconcileTimer = setTimeout(() => {
    resumeReconcileTimer = null
    const state = get()
    if (state.currentSessionId !== sessionId) return
    if (!state.sendingMessage) return
    // A frame arrived at or after the resume → the turn is alive and
    // turn_complete will finalize it. Silent → it finished during the load
    // gap; reconcile to fetch the persisted answer.
    if (state.lastStreamEventAt >= resumedAt) return
    void state.mutations.reconcileInFlightTurn(slug, sessionId)
  }, RESUME_RECONCILE_DELAY_MS)
}

function replaceSession(
  sessions: ChatSession[],
  updated: ChatSession,
): ChatSession[] {
  return sessions.map((session) =>
    session.id === updated.id ? updated : session,
  )
}

// Active sessions read newest-created first, matching lukita's `recent` scope.
function byCreatedAtDesc(left: ChatSession, right: ChatSession): number {
  return right.createdAt.localeCompare(left.createdAt)
}

export const sessionsMutations: ChatStoreCreator<SessionsMutations> = (
  set,
) => ({
  async createSession(slug: string, options = {}) {
    const session = await chatApi.createSession(slug, {
      title: options.title,
      agent: options.agent,
    })
    set((state) => ({ sessions: [session, ...state.sessions] }))
    return session
  },

  async renameSession(slug: string, id: string, title: string) {
    const updated = await chatApi.renameSession(slug, id, title)
    set((state) => ({ sessions: replaceSession(state.sessions, updated) }))
  },

  async archiveSession(slug: string, id: string) {
    await chatApi.archiveSession(slug, id)
    set((state) => {
      const archived = state.sessions.find((session) => session.id === id)
      return {
        sessions: state.sessions.filter((session) => session.id !== id),
        // Keep the Archived section in sync without a refetch: move the row in
        // with a freshly-stamped archivedAt so it sorts to the top.
        archivedSessions: archived
          ? [
              { ...archived, archivedAt: new Date().toISOString() },
              ...state.archivedSessions,
            ]
          : state.archivedSessions,
        currentSessionId:
          state.currentSessionId === id ? null : state.currentSessionId,
        messages: state.currentSessionId === id ? [] : state.messages,
      }
    })
  },

  async unarchiveSession(slug: string, id: string) {
    const restored = await chatApi.unarchiveSession(slug, id)
    set((state) => ({
      archivedSessions: state.archivedSessions.filter(
        (session) => session.id !== id,
      ),
      sessions: [...state.sessions, restored].sort(byCreatedAtDesc),
    }))
  },

  async pinSession(slug: string, id: string) {
    const updated = await chatApi.pinSession(slug, id)
    set((state) => ({ sessions: replaceSession(state.sessions, updated) }))
  },

  async unpinSession(slug: string, id: string) {
    const updated = await chatApi.unpinSession(slug, id)
    set((state) => ({ sessions: replaceSession(state.sessions, updated) }))
  },

  clearCurrentSession() {
    set({ currentSessionId: null, messages: [] })
  },

  async cancelReportEdit(slug: string, token: string) {
    set((state) => ({
      cancelledReportEditTokens: [...state.cancelledReportEditTokens, token],
    }))
    try {
      await chatApi.cancelReportEdit(slug, token)
    } catch (error) {
      set((state) => ({
        cancelledReportEditTokens: state.cancelledReportEditTokens.filter(
          (cancelled) => cancelled !== token,
        ),
      }))
      throw error
    }
  },
})
